import React, { useEffect } from 'react';
import GeneralImageNew from '@gp/components/GeneralImageNew';
import MultipleLinesTextBubble from '@gp/components/MultipleLinesTextBubble';
import LinkJump from '@gp/components/LinkJump';
import TriggerFoldBtn from '@gp/components/TriggerFoldBtn';
import classNames from 'classnames';
import {
  getFormatNumPlus,
  queryJumpable,
  isValidNumber,
  getNumberOfDotUnit,
} from '@gp/utils/common';
import { FIELD_NAMES, FIELDS1 } from '@gp/utils/metricTips';
import { history } from 'umi';
import { MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';
import { TABLE_HEAD_MAP, METRIC_KEYS, METRIC_TIPS_CFG_MAPPING } from '../constants';

function getTableList(data: IStudioOverallPeopleListMetricsResultData) {
  const resultList = [];
  if (Array.isArray(data) && data?.length) {
    for (let i = 0; i < data?.length; i++) {
      const t = data?.[i];
      const studioMetrics = t?.studio_metrics; // studio_metrics后端排序
      if (Array.isArray(studioMetrics)) {
        for (let j = 0; j < studioMetrics?.length; j++) {
          const k = studioMetrics?.[j];
          const isLast = j === studioMetrics?.length - 1;
          const item = {
            uniqueId: `${k?.studio?.id}_${i}_${j}`,
            boName: t?.management_team,
            isSpacerBlock: isLast,
            isAllTotal: k?.studio?.name?.toLowerCase() === 'all studios',
            isTotal:
              k?.studio?.name?.toLowerCase() === 'sub-total' ||
              k?.studio?.name?.toLowerCase() === 'all studios',
            studioId: k?.studio?.id,
            studioName: k?.studio?.name,
            studioCover: k?.studio?.cover,
            ...k,
          };
          // @ts-ignore
          delete item.studio;
          resultList.push(item);
        }
      }
    }
  }

  return resultList;
}

// 获取第一行固定行每列的展示结果
const getFirstRowTdV = (
  firstLineList: { [keyName: string]: string },
  keyName: string,
  format: 0 | 1 | 2, // 0: 不需要处理, 1: 数值, 2: 需要转换B/M/K
): string => {
  switch (format) {
    case 0:
      return firstLineList?.[keyName] || '-';

    case 1:
      return isValidNumber(firstLineList?.[keyName])
        ? getNumberOfDotUnit(Math?.round(Number(firstLineList?.[keyName])))
        : '-';

    case 2:
      return getFormatNumPlus(firstLineList?.[keyName]) || '-';

    default:
      return firstLineList?.[keyName] || '-';
  }
};

const toDetail = (
  studioId: string,
  studioName: string,
  isJump: boolean = true,
) => {
  if (!studioId) return '';
  const filter = encodeURIComponent(
    JSON.stringify({
      studioId,
      studioName,
      menuFrom: MENU_ID_MAP['studio-dashboard_studio-overall'], // 用于标识左侧菜单栏当前选中高亮
      tab: 'people',
    }),
  );
  const url = SLASH_G_POP + `/Studio/PortfolioOverview/Detail?filter=${filter}`;
  if (isJump) {
    history.push(url);
  }

  return url;
};

// 获取列配置
const getColumns = (
  dateList: any,
  unFoldList: string[],
  setUnFoldList: React.Dispatch<React.SetStateAction<string[]>>,
  triggerFoldExpand: boolean,
) => {
  useEffect(() => {
    if (triggerFoldExpand) {
      // 展开最后一项时，往右滚动到最右边，让用户知道后面还有展开的列，增强用户体验
      const t = setTimeout(() => {
        const tableBodyEl: any = document.querySelector(
          '.dbg-studio-overall-people-list-table .tea-table__body',
        );
        if (tableBodyEl) {
          const currentWidth = tableBodyEl.offsetWidth,
            scrollLeft = tableBodyEl.scrollLeft,
            scrollWidth = tableBodyEl.scrollWidth;
          // 出现滚动条时，此时自动向右滚动到最右边
          if (scrollWidth - currentWidth > 0) {
            tableBodyEl.scrollLeft = scrollWidth - currentWidth;
          }
        }
        clearTimeout(t);
      }, 200);
    }
  }, [triggerFoldExpand]);

  // 数值列配置
  const valueColOpt =
    Object?.entries(TABLE_HEAD_MAP)
      ?.filter((t) => {
        return t?.[1]?.isValueCol;
      })
      ?.map((t) => {
        const itemOpt = t?.[1];
        // 展开项中存在或者拥有子类时，正常宽度显示，否则宽度为0不展示
        const width = !triggerFoldExpand
          ? unFoldList?.includes(itemOpt.groupKey) ||
            itemOpt.isGroup ||
            !itemOpt.groupKey
            ? itemOpt.width
            : 0
          : itemOpt.width;

        return {
          key: itemOpt?.keyName,
          header: () => {
            const titles = itemOpt?.title;

            const headerContext = (
              <div className="dbg-table-header-text">
                {titles?.[0]?.includes('-') && dateList?.[itemOpt?.keyName]
                  ? titles?.[0]?.replace('-', dateList?.[itemOpt?.keyName])
                  : titles?.[0]}
                <br/>
                {titles?.[1]}
              </div>
            );

            if (!METRIC_KEYS.includes(itemOpt?.keyName)) return headerContext;

            const { definition, source, updateCadence } = METRIC_TIPS_CFG_MAPPING[itemOpt?.keyName] || {};

            return (
              <MultipleLinesTextBubble fields={FIELDS1} names={FIELD_NAMES} data={{ definition, source, updateCadence }}>
                {headerContext}
              </MultipleLinesTextBubble>
            );

            return (
              <div className="dbg-table-header-text">
                {titles?.[0]?.includes('-') && dateList?.[itemOpt?.keyName]
                  ? titles?.[0]?.replace('-', dateList?.[itemOpt?.keyName])
                  : titles?.[0]}
                <br />
                {titles?.[1]}
              </div>
              // {
              // !!itemOpt.isGroup && (
              //   <section
              //     className="header-fold-btn"
              //     style={{zIndex: (itemOpt.keyName === 'ytm_staff_cost' && !unFoldList?.includes('ytm_staff_cost'))/* 展开项中不包含ytm_staff_cost */ ? 0 : 10}}
              //     onClick={(e: any)=>{e.stopPropagation()}}
              //   >
              //     {/* <TriggerFoldBtn
              //       state={unFoldList?.includes(itemOpt?.keyName)}
              //       onClick={() => {
              //         // 判断展开列表中是否存在，存在的话，删除展开列表中的key，不存在的话，把key加到展开项中
              //         const index = unFoldList?.findIndex((item: string) => (item === itemOpt.keyName));
              //         const newUnFoldList = index >= 0 ? unFoldList?.filter((item: string) => (item !== itemOpt.keyName)) : [...unFoldList, itemOpt.keyName];
              //         setUnFoldList(newUnFoldList);
              //         // 展开最后一项时，往右滚动到最右边，让用户知道后面还有展开的列，增强用户体验
              //         if (index === -1 && itemOpt?.keyName === 'ytm_staff_cost') {
              //           const t = setTimeout(() => {
              //             const tableBodyEl: any = document.querySelector('.dbg-studio-overall-people-list-table .tea-table__body');
              //             if (tableBodyEl) {
              //               const currentWidth = tableBodyEl.offsetWidth,
              //                     scrollLeft = tableBodyEl.scrollLeft,
              //                     scrollWidth = tableBodyEl.scrollWidth;
              //               // 出现滚动条时，此时自动向右滚动到最右边
              //               if ((scrollWidth - currentWidth) > 0) {
              //                 tableBodyEl.scrollLeft = scrollWidth - currentWidth;
              //               }
              //             }
              //             clearTimeout(t);
              //           }, 200);
              //         }
              //       }}
              //     /> */}
              //   </section>
              // )
              // }
            );
          },
          align: itemOpt?.align,
          width,
          render: (cvm: any) => {
            const v =
              (itemOpt?.isFormat
                ? getFormatNumPlus(cvm?.[itemOpt?.keyName])
                : isValidNumber(cvm?.[itemOpt?.keyName])
                ? getNumberOfDotUnit(
                    Math?.round(Number(cvm?.[itemOpt?.keyName])),
                  )
                : '-') || '-';

            return <>{v}</>;
          },
        };
      }) || [];

  return [
    {
      key: TABLE_HEAD_MAP?.['bo']?.keyName,
      header: () => {
        const titles = TABLE_HEAD_MAP?.['bo']?.title;

        return (
          <div className="dbg-table-header-text">
            {titles?.[0]}
            <br />
            {titles?.[1]}
          </div>
        );
      },
      align: TABLE_HEAD_MAP?.['bo']?.align,
      width: TABLE_HEAD_MAP?.['bo']?.width,
      // fixed: 'left', //和动态计算合并单元格冲突，不能使用
      render: (cvm: any) => {
        return (
          <>
            <div className="dbg-table-bold">
              {cvm?.isTotal ? cvm?.boName || '-' : ''}
            </div>
          </>
        );
      },
    },
    {
      key: TABLE_HEAD_MAP?.['studio']?.keyName,
      header: () => {
        const titles = TABLE_HEAD_MAP?.['studio']?.title;

        return (
          <div className="dbg-table-header-text">
            {titles?.[0]}
            <br />
            {titles?.[1]}
          </div>
        );
      },
      align: TABLE_HEAD_MAP?.['studio']?.align,
      width: TABLE_HEAD_MAP?.['studio']?.width,
      // fixed: 'left', //和动态计算合并单元格冲突，不能使用
      render: (cvm: any) => {
        return (
          <div className="dbg-table-studio-box">
            {!cvm?.isTotal && (
              <LinkJump url={toDetail(cvm?.studioId, cvm?.studioName, false)}>
                <GeneralImageNew
                  className="gp-cursor-pointer"
                  domain="studio"
                  width={24}
                  height={24}
                  src={cvm?.studioCover}
                  onClick={(e: React.MouseEvent<Element, MouseEvent>) => {
                    if (!queryJumpable(e)) return;
                    toDetail(cvm?.studioId, cvm?.studioName);
                  }}
                />
              </LinkJump>
            )}
            <LinkJump url={toDetail(cvm?.studioId, cvm?.studioName, false)}>
              <span
                className={`dbg-table-studio-text ${
                  cvm?.isTotal ? '' : 'dbg-table-studio-name dbg-cursor-pointer'
                }`}
                title={cvm?.studioName || '-'}
                onClick={(e) => {
                  if (!queryJumpable(e)) return;
                  toDetail(cvm?.studioId, cvm?.studioName);
                }}
              >
                {cvm?.studioName || '-'}
              </span>
            </LinkJump>
          </div>
        );
      },
    },
    ...valueColOpt,
  ];
};

export { getTableList, getFirstRowTdV, getColumns };
